import { all, call, put, select, fork, takeLatest } from 'redux-saga/effects';
import { authPageActions as actions } from '.';
import jwtDecode from 'jwt-decode';
import api from 'app/api';
import { JwtPayload, catalogItemInfo } from './types';
import { fetchCurrentEmployee } from 'utils/apiCall/fetchCurrentEmployee';
import { RootState } from 'types';
import { inboxPageActions } from 'app/pages/Inbox/slice';
import { settingsActions } from 'app/pages/Settings/slice';
import { googleSheetURLToJson } from 'utils/googleSheetToJson';
import { ErrorInterface, CustomError } from 'utils/types/success-error-types';
import { getDomainConfig } from 'utils/whiteLabel';
import { setIdentityToken, setTabToken } from 'utils/tabAuth';
import { claimBusiness } from 'utils/tabBusinessRegistry';
import {
  decodeChatParam,
  encodeChatParam,
  CHAT_URL_PARAM,
} from 'utils/chatUrlEncoder';

export function* processLoginToken(userToken: string) {
  const info: JwtPayload = jwtDecode(userToken);
  info.token = userToken;

  setTokenToLocalStorage(userToken);

  // Partner dashboard: just need auth, skip all business checks
  if (getDomainConfig().isPartnerDashboard) {
    info.isLoggedIn = true;
    info.isBusinessIdNotInToken = false;
    yield put(actions.signInSuccess(info));
    return;
  }

  if (!info?.business?.id) {
    info.isBusinessIdNotInToken = true;
    yield put(actions.signInSuccess(info));
    yield put(actions.getOrgBusinessStart());
    return;
  }
  info.isLoggedIn = true;
  info.isBusinessIdNotInToken = false;

  const bizResponse = yield getBizDetails();
  if (
    !(
      bizResponse?.data?.phoneNumberId &&
      bizResponse?.data?.businessAccountId &&
      bizResponse?.data?.fbAppId
    )
  ) {
    window.location.href = '/settings/1';
    return;
  }
  yield put(actions.signInSuccess(info));
  yield put(actions.getCurrentEmployeeStart());
  yield put(actions.getRolePermissionListStart());
  yield put(inboxPageActions.getTemplatesStart());
  yield put(inboxPageActions.getTemplateMediaLinkStart());
  yield put(
    inboxPageActions.updateSentBy({
      id: info.id,
      name: info.name,
      businessId: info.business.id,
    }),
  );
}

function handleSignInError(error: ErrorInterface, fallbackMessage: string) {
  return actions.signInFailure(
    (error?.response?.data as CustomError) ?? {
      errorType: 'Error',
      errorMessage: fallbackMessage,
      errorRaw: null,
      errorsValidation: null,
    },
  );
}

function* signIn(action: {
  payload: { email: string; password: string; token?: string };
  type: string;
}) {
  try {
    const { email, password, token } = action.payload;

    const response = yield call(
      api.post,
      `/auth/login`,
      { email, password, token },
      { withCredentials: true },
    );

    if (response?.data?.data?.requiresTwoFA) {
      yield put(actions.requires2FA());
      return;
    }

    yield processLoginToken(response.data.data.token);
  } catch (err: unknown) {
    yield put(handleSignInError(err as ErrorInterface, 'Sign in failed'));
  }
}

function* googleSignIn(action: {
  payload: { credential: string; twoFAToken?: string };
  type: string;
}) {
  try {
    const { credential, twoFAToken } = action.payload;

    const response = yield call(
      api.post,
      `/auth/google-login`,
      { credential, twoFAToken },
      { withCredentials: true },
    );

    if (response?.data?.data?.requiresTwoFA) {
      yield put(actions.googleRequires2FA({ credential }));
      return;
    }

    yield processLoginToken(response.data.data.token);
  } catch (err: unknown) {
    yield put(
      handleSignInError(err as ErrorInterface, 'Google sign in failed'),
    );
  }
}

function* updateToken() {
  try {
    const response = yield call(api.get, `/auth/update-token`);
    const userToken = response.data.data;
    const info: JwtPayload = jwtDecode(userToken);
    info.token = userToken;
    info.isLoggedIn = true;

    setTokenToLocalStorage(userToken);
    yield put(actions.signInSuccess(info));
    yield put(
      inboxPageActions.updateSentBy({
        id: info.id,
        name: info.name,
        businessId: info.business?.id ?? 0,
      }),
    );
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    console.error('updateToken error:', error?.response?.data);
  }
}

function* getBizDetails() {
  try {
    const response = yield call(api.get, `/business`);
    const business = response.data.data;
    yield put(actions.getBusinessDetailsSuccess(business));
    let items: catalogItemInfo[] = [];
    if (business?.catalogSheetLink && business?.catalogSheetLink?.length > 10) {
      const fetchCatalogItems = async () => {
        items = await googleSheetURLToJson(business.catalogSheetLink || '');
      };
      yield fetchCatalogItems();
      yield put(actions.setCatalogItemsSuccess(items));
    }
    return response?.data;
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get business details',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getCurrentEmployeeDetails() {
  try {
    const email: string | undefined = yield select(
      (state: RootState) => state.authPage?.authData?.email,
    );
    if (!email) {
      yield put(actions.getCurrentEmployeeSuccess(null));
      return;
    }
    const employee = yield call(fetchCurrentEmployee, email);
    yield put(actions.getCurrentEmployeeSuccess(employee));
  } catch {
    yield put(actions.getCurrentEmployeeSuccess(null));
  }
}

function* getOrgBusinessDetails() {
  try {
    const response = yield call(api.get, `/org/business`);
    const businesses = response.data.data;
    // Last-business auto-login is resolved server-side now (login picks the
    // employee's most recent session's business), so the frontend just lists.
    yield put(actions.getOrgBusinessSuccess(businesses));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.getOrgBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get businesses',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getSelectBusinessToken(action: {
  payload: { businessId: number; keepChat?: boolean };
  type: string;
}) {
  try {
    const { businessId, keepChat } = action.payload;
    // Switching is tab-scoped: pin the new business to THIS tab only. Always
    // parallel so the token this tab is still using mid-switch is never revoked
    // — that revoke races with in-flight requests and surfaces a spurious
    // "session expired". Orphaned sessions just expire on their own.
    const response = yield call(
      api.get,
      `/org/business-token/${businessId}?parallel=1`,
    );
    setTabToken(response.data.data.token);
    // Pin the new business to THIS tab only — non-exclusive, so other tabs
    // already on this business keep running (multiple tabs per business).
    claimBusiness(businessId);
    sessionStorage.removeItem('selectedClientWaNumber');
    const url = new URL(window.location.href);
    const chatParam = url.searchParams.get(CHAT_URL_PARAM);
    if (keepChat && chatParam) {
      // Follow the open chat across the switch (chat-link redirect, or a navbar
      // business switch): re-stamp it with the business we switched to — same
      // chat number, new business id — so the same contact opens there. If that
      // contact has no chat in the new business, getSingleClient falls back to a
      // stub rather than erroring.
      try {
        const { clientWaNumber } = decodeChatParam(chatParam);
        url.searchParams.set(
          CHAT_URL_PARAM,
          encodeChatParam(clientWaNumber, businessId),
        );
      } catch {
        // leave the param as-is if it can't be decoded
      }
    } else {
      // Entry-point switch (choosing a business, or landing on a freshly created
      // one): no contact to carry over, so drop any stale chat param.
      url.searchParams.delete(CHAT_URL_PARAM);
      url.searchParams.delete('clientWaNumber');
    }
    window.location.replace(url.pathname + url.search + url.hash);
  } catch (err: unknown) {
    yield put(
      handleSignInError(err as ErrorInterface, 'Failed to get business token'),
    );
  }
}

function* addNewBusiness() {
  try {
    const response = yield call(api.post, `/org/business`, {});
    const { businesses, newBusinessId } = response.data.data;

    // Update the businesses list
    yield put(actions.getOrgBusinessSuccess(businesses));

    // Auto-select the newly created business
    if (newBusinessId) {
      yield put(actions.getBusinessTokenStart({ businessId: newBusinessId }));
    }
  } catch (err) {
    const error = err as ErrorInterface;
    yield put(
      actions.getOrgBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to create business',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* updateBusinessName(action: {
  payload: { businessId: number; name: string };
  type: string;
}) {
  try {
    const { businessId, name } = action.payload;
    const response = yield call(api.put, `/org/business/${businessId}`, {
      name,
    });
    yield put(actions.updateBusinessNameSuccess(response.data.data));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.updateBusinessNameFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to update business name',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* deleteBusiness(action: {
  payload: { businessId: number };
  type: string;
}) {
  try {
    const { businessId } = action.payload;
    const response = yield call(api.delete, `/org/business/${businessId}`);
    yield put(actions.deleteBusinessSuccess(response.data.data));
  } catch (err) {
    const error = err as ErrorInterface;
    yield put(
      actions.deleteBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to delete business',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

// 2FA Saga Functions
function* enable2FA() {
  try {
    const response = yield call(api.get, `/auth/2fa/enable`);
    yield put(actions.enable2FASuccess(response.data.data));
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.enable2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to enable 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* verify2FA(action: { payload: { token: string }; type: string }) {
  try {
    const { token } = action.payload;
    yield call(api.post, `/auth/2fa/verify`, { token });
    yield put(actions.verify2FASuccess());
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.verify2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to verify 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* disable2FA(action: { payload: { token: string }; type: string }) {
  try {
    const { token } = action.payload;
    yield call(api.post, `/auth/2fa/disable`, { token });
    yield put(actions.disable2FASuccess());
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.disable2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to disable 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getRolePermissionList() {
  try {
    const response = yield call(api.get, `/role-permission`);
    yield put(actions.getRolePermissionListSuccess(response.data.data));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get role permissions',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

export function setTokenToLocalStorage(token: string) {
  setIdentityToken(`${token}`);
}

function* watchSignIn() {
  yield takeLatest(actions.signInStart.type, signIn);
}

function* watchGoogleSignIn() {
  yield takeLatest(actions.googleSignInStart.type, googleSignIn);
}

function* watchUpdateJwtToken() {
  yield takeLatest(actions.updateJwtTokenStart.type, updateToken);
}

function* watchGetBusinessDetails() {
  yield takeLatest(actions.getBusinessDetails.type, getBizDetails);
}

function* watchGetCurrentEmployee() {
  yield takeLatest(
    actions.getCurrentEmployeeStart.type,
    getCurrentEmployeeDetails,
  );
}

function* watchGetRolePermissionList() {
  yield takeLatest(
    actions.getRolePermissionListStart.type,
    getRolePermissionList,
  );
}

function* watchGetOrgBusinessDetails() {
  yield takeLatest(actions.getOrgBusinessStart.type, getOrgBusinessDetails);
}

function* watchGetSelectBusinessToken() {
  yield takeLatest(actions.getBusinessTokenStart.type, getSelectBusinessToken);
}

function* watchAddNewBusiness() {
  yield takeLatest(actions.addNewBusinessStart.type, addNewBusiness);
}

function* watchUpdateBusinessName() {
  yield takeLatest(actions.updateBusinessNameStart.type, updateBusinessName);
}

function* watchDeleteBusiness() {
  yield takeLatest(actions.deleteBusinessStart.type, deleteBusiness);
}

// 2FA Watchers
function* watchEnable2FA() {
  yield takeLatest(actions.enable2FAStart.type, enable2FA);
}

function* watchVerify2FA() {
  yield takeLatest(actions.verify2FAStart.type, verify2FA);
}

function* watchDisable2FA() {
  yield takeLatest(actions.disable2FAStart.type, disable2FA);
}

export function* authPageSaga() {
  const sagas = [
    watchSignIn,
    watchGoogleSignIn,
    watchUpdateJwtToken,
    watchGetBusinessDetails,
    watchGetCurrentEmployee,
    watchGetRolePermissionList,
    watchGetOrgBusinessDetails,
    watchGetSelectBusinessToken,
    watchAddNewBusiness,
    watchUpdateBusinessName,
    watchDeleteBusiness,
    watchEnable2FA,
    watchVerify2FA,
    watchDisable2FA,
  ];

  yield all(
    sagas.map(saga =>
      fork(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in authPageSaga:', e);
          }
        }
      }),
    ),
  );
}
